import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useMainContext } from '../../../hooks/useMainContext';
import RoundedButton from '../../Globle/RoundedButton';
import {
    AboveIcon,
    BelowIcon,
    DeleteIconSm,
    DuplicateIcon,
    MatrixIcon,
} from '../../Globle/Icon';
import { RiDragMove2Fill } from 'react-icons/ri';
import { GrPowerReset } from 'react-icons/gr';
import CustomTooltip from '../../Globle/CustomTooltip';
import { BsArrowsAngleExpand } from 'react-icons/bs';
import PageHandler from '../mapping/PageHandler';
import Button from '../../Globle/Button';
import { IoAddOutline } from 'react-icons/io5';
import { message, Popover, Table } from 'antd';
import CustomDropdown from '../../Globle/CustomDropdown';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { TfiDownload } from 'react-icons/tfi';

const leftMargin = 160;
const topMargin = 90;

const MatrixCalculator = observer(() => {
    const { closetManager, matrixManager } = useMainContext();
    const draggableRef = useRef(null);
    const [position, setPosition] = useState(
        matrixManager.tablePosition || { x: 20, y: 10 },
    );
    const [isOpen, setIsOpen] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [rows, setRows] = useState(() => {
        const existingData = matrixManager.matrixData || [];
        const maxExistingId = existingData.length
            ? Math.max(...existingData.map((row) => row.id))
            : 0;

        const emptyRows = Array.from({ length: 100 }, (_, index) => ({
            id: maxExistingId + index + 1,
            unitNumber: '',
            type: '',
            floor: '',
        }));

        return [...existingData, ...emptyRows];
    });
    const uniqueUnitNames = useMemo(() => {
        return Array.from(
            new Set(closetManager.units.map((unit) => unit.name)),
        ).filter(Boolean); // Removes falsy values (null, undefined, "")
    }, [closetManager.units]);
    const buttons = [
        {
            tooltip: 'Drag',
            onClick: () => {},
            icon: <RiDragMove2Fill className=" text-2xl" />,
            className: 'drag-handle',
        },
        {
            tooltip: 'Fit to Screen',
            onClick: async () => {
                await matrixManager.fitToScreen();
            },
            icon: <BsArrowsAngleExpand className="text-2xl" />,
            className: '',
        },

        {
            tooltip: 'Reset Position',
            onClick: () => {
                matrixManager.resetUnitTablePosition();
            },
            icon: <GrPowerReset className="text-2xl" />,
            className: '',
        },
    ];

    useEffect(() => {
        if (matrixManager.resetTablePositionTriggered) {
            const defaultPosition = { x: 20, y: 10 };
            setPosition(defaultPosition);
            matrixManager.setTablePosition(defaultPosition);
            matrixManager.clearResetTablePosition();
        }
    }, [matrixManager, matrixManager.resetTablePositionTriggered]);
    useEffect(() => {
        const validRows = rows.filter(
            (row) => row.type && row.type !== 'Select Unit',
        );

        const seen = new Set();
        for (const row of validRows) {
            const type = row.type.toString().trim().toLowerCase();
            seen.add(type);
        }

        matrixManager.setMatrixData(validRows);
    }, [rows, matrixManager]);
    const handleDrag = (e) => {
        const newPosition = {
            x: e.clientX - leftMargin,
            y: e.clientY - topMargin,
        };
        setPosition(newPosition);
        // matrixManager.setTablePosition(newPosition);
    };
    const handleChange = (id, field, value) => {
        if (field === 'type') {
            value = value === '' ? null : value;
        } else if (field === 'unitNumber') {
            const numericRegex = /^[0-9]{0,10}$/;
            if (!numericRegex.test(value)) {
                return;
            }
        } else {
            const regex =
                /^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{0,10}$/;
            if (!regex.test(value)) {
                return;
            }
        }
        setRows((prevRows) =>
            prevRows.map((row) =>
                row.id === id ? { ...row, [field]: value } : row,
            ),
        );
    };
    const handleAddAllUnits = () => {
        // Get all unique unit types from closetManager
        const allUnitTypes = Array.from(
            new Set(closetManager.units.map((unit) => unit.name)),
        ).filter(Boolean);

        // Get currently used unit types from the table
        const usedUnitTypes = new Set(
            rows
                .filter((row) => row.type)
                .map((row) => row.type.toString().trim().toLowerCase()),
        );

        // Find unused unit types
        const unusedUnitTypes = allUnitTypes.filter(
            (type) => !usedUnitTypes.has(type.toString().trim().toLowerCase()),
        );

        if (unusedUnitTypes.length === 0) {
            message.info('All unit types are already in the table');
            return;
        }
        const hasValidEntries = rows.some((row) => row.type);
        const insertIndex = hasValidEntries
            ? rows.reduce(
                  (lastIndex, row, currentIndex) =>
                      row.type ? currentIndex : lastIndex,
                  -1,
              ) + 1
            : 0;

        const maxExistingId = Math.max(...rows.map((row) => row.id));
        const newRows = unusedUnitTypes.map((type, index) => ({
            id: maxExistingId + index + 1,
            unitNumber: '',
            type: type,
            floor: '',
        }));

        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            updatedRows.splice(insertIndex, 0, ...newRows);
            return updatedRows;
        });
        message.success(`Added ${unusedUnitTypes.length} new unit types`);
    };
    const handleAddMoreRows = () => {
        // Find the last valid entry (one that has a type)
        const lastValidIndex =
            rows.reduce(
                (lastIndex, row, currentIndex) =>
                    row.type ? currentIndex : lastIndex,
                -1,
            ) + 1;
        const insertIndex = lastValidIndex === 0 ? rows.length : lastValidIndex;

        const maxExistingId = Math.max(...rows.map((row) => row.id));
        const newRows = Array.from({ length: 20 }, (_, index) => ({
            id: maxExistingId + index + 1,
            unitNumber: '',
            type: '',
            floor: '',
        }));

        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            updatedRows.splice(insertIndex, 0, ...newRows);
            return updatedRows;
        });
    };
    const handleAddRowAbove = (targetId) => {
        const targetIndex = rows.findIndex((row) => row.id === targetId);
        if (targetIndex === -1) return;

        const maxExistingId = Math.max(...rows.map((row) => row.id));
        const newRow = {
            id: maxExistingId + 1,
            unitNumber: '',
            type: '',
            floor: '',
        };

        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            updatedRows.splice(targetIndex, 0, newRow); // Insert above
            return updatedRows;
        });
        message.info('Row added above');
        setIsOpen(false);
    };
    const handleAddRowBelow = (targetId) => {
        const targetIndex = rows.findIndex((row) => row.id === targetId);
        if (targetIndex === -1) return;

        const maxExistingId = Math.max(...rows.map((row) => row.id));
        const newRow = {
            id: maxExistingId + 1,
            unitNumber: '',
            type: '',
            floor: '',
        };

        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            updatedRows.splice(targetIndex + 1, 0, newRow); // Insert below
            return updatedRows;
        });
        message.info('Row added below');
        setIsOpen(false);
    };
    const handleDuplicateRow = (targetId) => {
        const targetRow = rows.find((row) => row.id === targetId);
        if (!targetRow) return;

        const maxExistingId = Math.max(...rows.map((row) => row.id));
        const newRow = {
            id: maxExistingId + 1,
            unitNumber: targetRow.unitNumber,
            type: targetRow.type,
            floor: targetRow.floor,
        };

        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            const targetIndex = updatedRows.findIndex(
                (row) => row.id === targetId,
            );
            updatedRows.splice(targetIndex + 1, 0, newRow); // Duplicate below
            return updatedRows;
        });
        message.info('Row duplicated below');
        setIsOpen(false);
    };
    const handleDeleteRow = (targetId) => {
        if (rows.length <= 1) {
            message.warning('At least one row must remain');
            return;
        }

        setRows((prevRows) => prevRows.filter((row) => row.id !== targetId));
        message.success('Row deleted');
    };

    const columns = [
        {
            title: 'Unit',
            dataIndex: 'unitNumber',
            key: 'unitNumber',
            width: 72,
            render: (text, row) => (
                <input
                    type="text"
                    value={row.unitNumber}
                    onChange={(e) =>
                        handleChange(row.id, 'unitNumber', e.target.value)
                    }
                    className="table-input w-full bg-theme-header-bg h-[32px] px-[8px] text-[14px] font-[400] rounded-[8px] focus:outline-none focus:ring-1 focus:ring-theme-button-bg"
                />
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 176,
            render: (text, row) => {
                const normalizedOptions = uniqueUnitNames.map((option) => ({
                    label: option,
                    value: option,
                }));
                return (
                    <CustomDropdown
                        options={normalizedOptions}
                        value={row.type}
                        onChange={(value) => {
                            handleChange(row.id, 'type', value);
                        }}
                        variant="borderless"
                        rootClassName="w-full bg-theme-header-bg rounded-[8px] px-[8px] table-input"
                    />
                );
            },
        },
        {
            title: 'Floor',
            dataIndex: 'floor',
            key: 'floor',
            width: 72,
            render: (text, row) => (
                <input
                    className="w-full table-input bg-theme-header-bg h-[32px] px-[8px] text-[14px] font-[400] rounded-[8px] focus:outline-none focus:ring-1 focus:ring-theme-button-bg"
                    type="text"
                    value={row.floor}
                    onChange={(e) =>
                        handleChange(row.id, 'floor', e.target.value)
                    }
                />
            ),
        },
        {
            title: '',
            dataIndex: 'action',
            key: 'action',
            width: 40,
            render: (text, row) => (
                <Popover
                    rootClassName="row-action-popover"
                    onOpenChange={(open) => setIsOpen(open)}
                    open={isOpen && row.id === selectedRowId}
                    content={
                        <PopoverContent
                            rowId={row.id}
                            handleAddRowAbove={handleAddRowAbove}
                            handleAddRowBelow={handleAddRowBelow}
                            handleDuplicateRow={handleDuplicateRow}
                            handleDeleteRow={handleDeleteRow}
                        />
                    }
                    trigger="click"
                    placement="bottomRight"
                    arrow={false}>
                    <RoundedButton
                        icon={
                            <BsThreeDotsVertical
                                className={`text-[16px] group-hover:text-white  ${isOpen && row.id === selectedRowId ? '!text-white' : ''}`}
                            />
                        }
                        className={`group ${isOpen && row.id === selectedRowId ? '!bg-theme-black' : ''} `}
                        onClick={() => {
                            setSelectedRowId(row.id);
                        }}
                    />
                </Popover>
            ),
        },
    ];
    return (
        <Draggable
            handle=".drag-handle"
            position={position}
            onDrag={handleDrag}
            nodeRef={draggableRef}>
            <div
                ref={draggableRef}
                className="absolute w-[460px] h-[calc(100vh-180px)] bg-theme-white rounded-[20px] p-[24px] flex flex-col shadow"
                style={{ zIndex: 1000, willChange: 'transform' }}>
                <div className="flex items-center justify-between gap-[8px] mb-[16px] flex-shrink-0">
                    <div className="flex items-center gap-[8px]">
                        <RoundedButton
                            icon={<MatrixIcon />}
                            className="w-[48px] h-[48px]"
                            disabled={true}
                        />
                        <h1 className="font-favorit text-[20px] font-[700] leading-[28px] text-theme-black">
                            Matrix Calculator
                        </h1>
                    </div>
                    <div className="flex items-center gap-[8px]">
                        {buttons.map((button, index) => (
                            <CustomTooltip
                                key={index}
                                label={button.tooltip}
                                placement="top"
                                styles={{
                                    background: '#000000',
                                    padding: '5px 8px',
                                    fontSize: '12px',
                                    color: '#fff',
                                    lineHeight: '14px',
                                }}>
                                <RoundedButton
                                    onClick={button.onClick}
                                    icon={button.icon}
                                    className={`w-[48px] h-[48px] mapping-icon-button ${button.className}`}
                                />
                            </CustomTooltip>
                        ))}
                    </div>
                </div>
                <div className="flex gap-[8px] justify-between flex-shrink-0">
                    <PageHandler />
                    <div className="flex items-center gap-[8px]">
                        <Button
                            icon={IoAddOutline}
                            label={'Add All Unit'}
                            iconClass="!text-[20px] !ml-0 !mr-1 mb-[3px]"
                            className={
                                '!h-[32px] !w-[120px] !px-[8px] !rounded-[8px] !bg-theme-header-bg hover:!bg-theme-button-bg hover:!text-black'
                            }
                            onClick={() => {
                                handleAddAllUnits();
                            }}
                        />
                        <Button
                            icon={TfiDownload}
                            label={'Download CSV'}
                            onClick={() => {
                                matrixManager.downloadCSV();
                            }}
                            iconClass="!text-[18px] !ml-0 !mr-1 mb-[3px]"
                            className={
                                '!h-[32px] !w-[140px] !px-[8px] !rounded-[8px] !bg-theme-header-bg hover:!bg-theme-button-bg hover:!text-black'
                            }
                        />
                    </div>
                </div>
                <Table
                    dataSource={rows || []}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                    scroll={{ y: 'calc(100vh - 405px)' }}
                    rowClassName={'matrix-calculator-table-row'}
                    className="mt-[16px] matrix-calculator-table"
                />
                <div className="flex gap-[8px] justify-end flex-shrink-0 mt-[14px]">
                    <Button
                        icon={IoAddOutline}
                        label={'New Units'}
                        onClick={() => {
                            handleAddMoreRows();
                        }}
                        iconClass="!text-[20px] !ml-0 !mr-1 mb-[3px]"
                        className={
                            '!h-[32px] !w-[110px] !px-[8px] !rounded-[8px] !bg-theme-header-bg hover:!bg-theme-button-bg hover:!text-black'
                        }
                    />
                </div>
            </div>
        </Draggable>
    );
});

export default MatrixCalculator;

const PopoverContent = observer(
    ({
        rowId,
        handleAddRowAbove,
        handleAddRowBelow,
        handleDeleteRow,
        handleDuplicateRow,
    }) => {
        const buttons = [
            {
                name: 'Insert Row Above ',
                icon: <AboveIcon />,
                onClick: () => {
                    handleAddRowAbove(rowId);
                },
            },
            {
                name: 'Insert Row Below',
                icon: <BelowIcon />,
                onClick: () => {
                    handleAddRowBelow(rowId);
                },
            },
            {
                name: 'Duplicate Row',
                icon: <DuplicateIcon />,
                onClick: () => {
                    handleDuplicateRow(rowId);
                },
            },
            {
                name: 'Delete Row',
                icon: <DeleteIconSm />,
                onClick: () => {
                    handleDeleteRow(rowId);
                },
            },
        ];
        return (
            <div className="flex flex-col gap-[8px] p-[8px] rounded-[8px] w-[200px]">
                {buttons.map((button, index) => (
                    <button
                        key={index}
                        className="flex items-center gap-[8px] cursor-pointer h-[37px] p-[8px] hover:bg-gray-200 rounded-[8px] transition-all duration-300 ease-in-out"
                        onClick={() => {
                            button.onClick();
                        }}>
                        {button.icon}
                        <p className="font-normal text-sm text-theme-black">
                            {button.name}
                        </p>
                    </button>
                ))}
            </div>
        );
    },
);
